import { CoreValue } from "@shared/models";

import { resolveAchievementBadgeArt } from "./achievementBadgeArt";
import { resolveCoreValueBadgeArt } from "./coreValueBadgeArt";
import { resolveFlameBadgeArt } from "./flameBadgeArt";
import { resolveTenureBadgeArt } from "./tenureBadgeArt";

/**
 * The per-category combinator behind `resolveBadgeIcon` (which is what `BadgeCard`,
 * `BadgeOverlayDetailsContainer` and `flameEmblemArt` call): a badge's bundled artwork, whatever its
 * category. Each category keeps its own resolver because they key differently:
 * flame, tenure and achievement codes are static strings, looked up verbatim; per-value codes embed an
 * environment-specific database id and need the core-value catalog to resolve (see
 * `coreValueBadgeArt.ts`).
 *
 * Adding a category means one more resolver and one more `??` here -- no caller changes.
 *
 * The order is not load-bearing: the resolvers' code spaces cannot overlap (`flame_*`, `tenure_*`,
 * `ach_*`, `value:<id>:t<tier>`), so at most one of them can ever return non-null for a given code.
 *
 * @param code - a badge's own `code`.
 * @param coreValues - the core-value catalog already in redux, needed only for per-value badges.
 * @returns the bundled asset URL, or `null` when no category has art for this code.
 */
export const resolveBadgeArt = (code: string, coreValues: CoreValue[]): string | null =>
  resolveFlameBadgeArt(code) ??
  resolveTenureBadgeArt(code) ??
  resolveAchievementBadgeArt(code) ??
  resolveCoreValueBadgeArt(code, coreValues);

/**
 * The icon a badge shows wherever it is drawn -- its tile, its detail overlay, the Lifetime Flame card:
 * the api's own `icon_url` when it sends one, otherwise the bundled art for its `code`, otherwise `null`.
 * One helper so those surfaces cannot disagree about a badge's picture if the rule ever changes.
 *
 * @param iconUrl - the badge's served `icon_url` (`null` for every category this app bundles art for).
 * @param code - the badge's own `code`. Optional because older test fixtures omit it; both view-model
 *   builders always set it. Absent means no bundled lookup at all.
 * @param coreValues - the core-value catalog, needed only for per-value badges.
 * @returns the URL to draw, or `null` for no image.
 */
export const resolveBadgeIcon = (
  iconUrl: string | null,
  code: string | undefined,
  coreValues: CoreValue[],
): string | null => iconUrl ?? (code ? resolveBadgeArt(code, coreValues) : null);
